import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/layout/PageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { StatusBadge } from "@/components/ui/Badge";
import { ApiError } from "@/lib/api/client";
import { listApplications } from "@/services/applications";
import {
  PARTNER_STATUS_LABELS,
  type ApplicationSummary,
  type PartnerApplicationStatus,
} from "@/types/application";

const statusOptions = [
  { value: "all", label: "All statuses" },
  ...Object.entries(PARTNER_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function ApplicationsPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | PartnerApplicationStatus>("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<ApplicationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listApplications({ q, status, page });
      setItems(data.results);
      setCount(data.count);
      setTotalPages(Math.max(1, data.total_pages));
    } catch (err) {
      setItems([]);
      setCount(0);
      setTotalPages(1);
      setError(
        err instanceof ApiError
          ? err.message
          : "Unable to load applications. Partner APIs may not be available yet.",
      );
    } finally {
      setLoading(false);
    }
  }, [q, status, page]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(handle);
  }, [load]);

  return (
    <div>
      <PageHeader
        eyebrow="Portfolio"
        title="Applications"
        description="Leads sourced by your organisation. Search and filter by current application status."
        actions={
          <Link to="/applications/new">
            <Button>Create lead</Button>
          </Link>
        }
      />

      <div className="mb-5 grid gap-3 rounded-2xl border border-line bg-surface-raised p-4 sm:grid-cols-[1fr_minmax(240px,320px)_auto]">
        <Input
          label="Search"
          name="q"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Company, email, or loan ID"
        />
        <Select
          label="Status"
          name="status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as "all" | PartnerApplicationStatus);
            setPage(1);
          }}
          options={statusOptions}
        />
        <div className="flex items-end">
          <Button variant="secondary" className="w-full" onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>

      {error ? <Alert className="mb-4">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner className="size-7" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No applications yet"
          description="Create a merchant lead to start tracking status and uploading onboarding documents."
          action={
            <Link to="/applications/new">
              <Button>Create your first lead</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-2xl border border-line bg-surface-raised">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-surface text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3 font-semibold">Merchant</th>
                  <th className="px-4 py-3 font-semibold">Loan ID</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {items.map((item) => (
                  <tr key={item.uuid} className="hover:bg-surface/70">
                    <td className="px-4 py-4">
                      <p className="font-medium capitalize text-ink">{item.company_name}</p>
                      <p className="text-xs capitalize text-muted">{item.merchant_name}</p>
                      <p className="text-xs text-muted">{item.merchant_email}</p>
                    </td>
                    <td className="px-4 py-4 text-muted">{item.loan_identifier || "—"}</td>
                    <td className="px-4 py-4">
                      <StatusBadge
                        status={item.status}
                        label={item.status_display}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-4 text-muted">{formatDate(item.created)}</td>
                    <td className="px-4 py-4 text-right">
                      <Link
                        to={`/applications/${item.uuid}`}
                        className="text-sm font-medium text-brand hover:text-brand-strong"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface-raised px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted">
              {count} application{count === 1 ? "" : "s"} · Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
